import Menu from '@mui/material/Menu';
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { ControlButton } from './ControlButton';
import { FilterOptionMenu } from './FilterOptionMenu';
import { FILTER_FAMILIES, type FilterFamilyId } from './filterFamilies';
import { FILTER_MENU_SLOT_PROPS } from './menuStyles';

interface FilterControlProps {
  label: string;
  family?: FilterFamilyId;
  tone?: string;
  options: string[];
  values: string[];
  valueLabels?: Record<string, string>;
  onChange: (values: string[]) => void;
  onRemove: () => void;
  onSearchChange?: (query: string) => void;
  autoOpen?: boolean;
  isLoading?: boolean;
  truncated?: boolean;
}

export function FilterControl({
  label,
  family,
  tone,
  options,
  values,
  valueLabels,
  onChange,
  onRemove,
  onSearchChange,
  autoOpen = false,
  isLoading = false,
  truncated = false,
}: FilterControlProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const resolvedTone = tone ?? (family === undefined ? undefined : FILTER_FAMILIES[family].tone);

  useEffect(() => {
    if (autoOpen) buttonRef.current?.click();
  }, [autoOpen]);

  const display = (value: string): string => valueLabels?.[value] ?? value;
  const [firstValue, ...restValues] = values;
  const summary =
    firstValue === undefined
      ? 'Any'
      : restValues.length === 0
        ? display(firstValue)
        : `${display(firstValue)} +${restValues.length}`;

  return (
    <>
      <ControlButton
        ref={buttonRef}
        label={label}
        value={summary}
        placeholder={values.length === 0}
        {...(resolvedTone === undefined ? {} : { tone: resolvedTone })}
        open={anchor !== null}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        onRemove={onRemove}
        fixedWidth
      />
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        slotProps={FILTER_MENU_SLOT_PROPS}
      >
        <FilterOptionMenu
          label={label}
          options={options.map((option) => ({ id: option, label: display(option) }))}
          selected={values}
          onChange={onChange}
          isLoading={isLoading}
          truncated={truncated}
          {...(onSearchChange ? { onSearchChange } : {})}
        />
      </Menu>
    </>
  );
}
